import type { MenuItemProps } from '@wix/editor-elements-definitions';

type DropdownItem = MenuItemProps & {
  id: string;
  items: Array<MenuItemProps>;
};

const checkHasItems = (
  maybeDropdown: MenuItemProps,
  multiple?: boolean,
): maybeDropdown is DropdownItem => {
  const dropdownItems = maybeDropdown.items ?? [];
  const amount = multiple ? 1 : 0;
  return dropdownItems.length > amount;
};

const checkHasSubItems = (dropdown: DropdownItem, multiple?: boolean) =>
  dropdown.items?.some(dropdownItem => {
    const amount = multiple ? 1 : 0;
    const dropdownSubItems = dropdownItem.items ?? [];
    return dropdownSubItems.length > amount;
  });

const checkIsAttached = (
  dropdown: DropdownItem,
  slots: Record<string, JSX.Element>,
) => {
  const { slot } = dropdown;
  if (!slot) {
    return true;
  }
  const dropdownCompId = slots?.[slot]?.props.id;
  const dropdownDomElement = document.getElementById(dropdownCompId);
  const computedStyle = getComputedStyle(dropdownDomElement!);
  const isDetached = computedStyle?.getPropertyValue('--isDetached')?.trim();
  return isDetached !== 'true' && isDetached !== '';
};

const PreviewParamWeights = {
  isAttached: 100,
  hasItems: 50,
  hasSubItems: 25,
  hasMultipleItems: 10,
  hasMultipleSubItems: 5,
  isPhysical: 1,
};

type PreviewParamWeightKeys = keyof typeof PreviewParamWeights;
type DropdownPreviewChecklist = { [key in PreviewParamWeightKeys]: boolean } & {
  id: string;
};

export const getDropdownToPreview = ({
  items = [],
  slots = {},
}: {
  items?: Array<MenuItemProps>;
  slots?: Record<string, JSX.Element>;
}) => {
  const filteredItems = items.filter(item => checkHasItems(item) || item.slot);
  const dropdownsPreviewChecklist: Array<DropdownPreviewChecklist> =
    filteredItems.map(dropdown => {
      const isPhysical = Boolean(dropdown.slot);
      return {
        id: dropdown.id!,
        isPhysical,
        hasItems: checkHasItems(dropdown),
        hasMultipleItems: checkHasItems(dropdown, true),
        hasSubItems: checkHasSubItems(dropdown as DropdownItem),
        hasMultipleSubItems: checkHasSubItems(dropdown as DropdownItem, true),
        isAttached: isPhysical
          ? checkIsAttached(dropdown as DropdownItem, slots)
          : true,
      };
    });

  const dropdownsPreviewScore = dropdownsPreviewChecklist.reduce(
    (acc, { id, ...nextChecklist }) => {
      return {
        ...acc,
        [id]: Object.entries(nextChecklist).reduce(
          (totalScore, [key, value]) => {
            const weightKey = key as PreviewParamWeightKeys;
            return value
              ? PreviewParamWeights[weightKey] + totalScore
              : totalScore;
          },
          0,
        ),
      };
    },
    {} as Record<string, number>,
  );

  const [dropdownToShow] = filteredItems.sort((dropdownA, dropdownB) => {
    const dropdownAScore = dropdownsPreviewScore[dropdownA.id!];
    const dropdownBScore = dropdownsPreviewScore[dropdownB.id!];
    return dropdownBScore - dropdownAScore;
  });

  return dropdownToShow;
};
